import React, { FC, memo, ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  ButtonContainer,
  ResidenceCardPreview,
  ResidenceCardStyled,
  ResidenceContent,
  ResidencePrice,
  ResidenceTitle
} from './styled';
import SvgIconLocation from '../../../public/static/images/residences/icon-location.svg';
import SvgIconDeadline from '../../../public/static/images/residences/icon-key.svg';
import SvgIconMetro from '../../../public/static/images/residences/metro.svg';
import SvgIconBrand from '../../../public/static/images/residences/brand.svg';
import { ResidenceParam } from './ResidenceParam';
import { CurrencySymbol } from '../../ui/ItemCard/CurrencySymbol';
import { CardPreviewImage } from '../../ui/ItemCard/styled';
import { withCacheBuster } from '../../ui/ItemCard/helpers';
import { useImageRetryOnReconnect } from '../../../hooks/useImageRetryOnReconnect';
import { Residence } from '../../../graphql/types';

export interface ResidenceCardProps
  extends Omit<Partial<Residence>, '__typename'> {
  hideExcessItems?: boolean;
  showPerUnit?: boolean;
  cypress?: string;
  stat: string;
  priority?: boolean;
  hideResidenceName?: boolean;
  button?: ReactNode;
}

export const ResidenceCard: FC<ResidenceCardProps> = memo(
  ({
    path,
    coverUrl,
    price,
    name,
    shortAddress,
    address,
    deadline,
    cypress,
    metroName,
    companyName,
    hideExcessItems = false,
    showPerUnit,
    stat,
    priority = false,
    hideResidenceName = false,
    button
  }) => {
    const { t } = useTranslation();
    const shownPrice = showPerUnit ? price?.perSqm : price?.total?.minimum;
    const formatPrice = shownPrice?.toLocaleString().replace(/,/g, ' ');
    const priceValue = shownPrice?.toLocaleString('ru-RU');
    const fontSizes = {
      price: 'h2',
      title: 'h3'
    };

    const { imgElementRef, retryCacheBuster } = useImageRetryOnReconnect();

    return (
      <ResidenceCardStyled
        $hideExcessItems={hideExcessItems}
        href={path}
        className="item-card_residence"
        cypress={cypress}
        stat={stat}
      >
        <ResidenceCardPreview>
          <CardPreviewImage
            ref={imgElementRef}
            src={withCacheBuster(coverUrl, retryCacheBuster)}
            alt={t('sections.residences.img_alt', { name })}
            width={460}
            height={345}
            {...(priority ? { fetchpriority: 'high' } : { loading: 'lazy' })}
          />
        </ResidenceCardPreview>
        <ResidenceContent>
          <ResidencePrice tag={fontSizes.price} data-cy={`${cypress}-price`}>
            <Trans
              i18nKey="sections.residences.from"
              values={{ price: priceValue }}
              components={{
                0: <span>{priceValue}</span>,
                1: <CurrencySymbol currency={price?.currency} />
              }}
            >
              <span>{formatPrice}</span>
              <CurrencySymbol currency={price?.currency} />
            </Trans>
            {showPerUnit && <span>{t('residences.per_unit')}</span>}
          </ResidencePrice>
          {!hideResidenceName && (
            <ResidenceTitle tag={fontSizes.title}>{name}</ResidenceTitle>
          )}
          {companyName && (
            <ResidenceParam
              icon={<SvgIconBrand />}
              param={companyName}
              hideOnMobile
            />
          )}
          {(shortAddress || address) && (
            <ResidenceParam
              icon={<SvgIconLocation />}
              param={shortAddress ? `${shortAddress}` : `${address}`}
            />
          )}
          {metroName && (
            <ResidenceParam
              icon={<SvgIconMetro />}
              param={metroName}
              hideOnMobile
            />
          )}
          {deadline && (
            <ResidenceParam icon={<SvgIconDeadline />} param={deadline} />
          )}
        </ResidenceContent>
        {button && <ButtonContainer>{button}</ButtonContainer>}
      </ResidenceCardStyled>
    );
  }
);

ResidenceCard.displayName = 'ResidenceCard';
